import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Trash2,
  Plus,
  Minus,
  ShoppingCart,
  CreditCard,
  Banknote,
  QrCode,
  Ticket,
  User,
  Search,
  CheckCircle,
  XCircle,
  Printer,
  AlertTriangle,
  LogOut,
  ArrowDownFromLine,
  ArrowUpFromLine,
} from 'lucide-react';
import { useVendaAtiva, usePdvOperacoes, usePdvState, useBuscarProduto, useBuscarCliente } from '@/features/pdv/hooks/usePdv';
import { usePdvOfflineCache } from '@/features/pdv/hooks/usePdvOfflineCache';
import { FormaPagamentoPdv, FormaPagamentoLabel, StatusPdvVenda } from '@/features/pdv/types/pdv.types';
import type { ProdutoPdv, ClientePdv, Venda, EstornoResultado, Turno, TurnoResumo } from '@/features/pdv/types/pdv.types';
import { pdvService } from '@/features/pdv/services/pdvService';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAuth } from '@/hooks/useAuth';
import { BannerConexao } from '@/features/pdv/components/BannerConexao';
import { offlineDb, type ItemOffline, type PagamentoOffline } from '@/lib/offlineDb';
import { salvarVendaOffline, sincronizarVendasPendentes } from '@/lib/syncManager';

// ════════════════════════════════════════════════════════════════════════════
//  CONSTANTS
// ════════════════════════════════════════════════════════════════════════════

// PDV-12: empresaId vem do usuário autenticado (claim empresa_id do JWT);
// caixaId identifica o caixa físico desta estação — persistido por terminal.
const CAIXA_STORAGE_KEY = 'udisys_pdv_caixa_id';

const ICONES_FORMA: Record<FormaPagamentoPdv, React.ReactNode> = {
  [FormaPagamentoPdv.Dinheiro]:      <Banknote className="h-4 w-4" />,
  [FormaPagamentoPdv.CartaoDebito]:  <CreditCard className="h-4 w-4" />,
  [FormaPagamentoPdv.CartaoCredito]: <CreditCard className="h-4 w-4" />,
  [FormaPagamentoPdv.Pix]:           <QrCode className="h-4 w-4" />,
  [FormaPagamentoPdv.Voucher]:       <Ticket className="h-4 w-4" />,
};

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ════════════════════════════════════════════════════════════════════════════
//  FrenteCaixa — layout em duas colunas (60% esquerda / 40% direita)
// ════════════════════════════════════════════════════════════════════════════

export default function FrenteCaixa() {
  const qc = useQueryClient();
  const state = usePdvState();
  const { vendaId, iniciarNovaSessao, encerrarSessao } = state;

  // Empresa logada (multi-empresa) e caixa físico desta estação
  const { user } = useAuth();
  const empresaId = user?.empresa.id ?? 0;
  const [caixaIdStr, setCaixaIdStr] = useState(
    () => localStorage.getItem(CAIXA_STORAGE_KEY) ?? '1',
  );

  const { data: venda, isLoading: carregandoVenda } = useVendaAtiva(vendaId);
  const ops = usePdvOperacoes(vendaId);

  // ── Offline / PWA (T2.6) ──────────────────────────────────────────────────
  const { online, statusConexao, vendasPendentes, conflitos } = useOnlineStatus();

  // Venda offline local (quando servidor indisponível)
  const [vendaOfflineItens,     setVendaOfflineItens]     = useState<ItemOffline[]>([]);
  const [vendaOfflinePagamentos, setVendaOfflinePagamentos] = useState<PagamentoOffline[]>([]);
  const [modoOffline,           setModoOffline]           = useState(false);
  const [vendaOfflineId,        setVendaOfflineId]        = useState<string | null>(null);
  const [vendaOfflineFinalizada, setVendaOfflineFinalizada] = useState(false);

  // Produtos/clientes vindos do cache offline
  const [produtosOfflineCache, setProdutosOfflineCache]   = useState<ProdutoPdv[]>([]);
  const [clientesOfflineCache, setClientesOfflineCache]   = useState<ClientePdv[]>([]);

  const totalOffline = useMemo(
    () => vendaOfflineItens.reduce((s, i) => s + i.precoUnitario * i.quantidade, 0),
    [vendaOfflineItens]
  );
  const totalPagoOffline = useMemo(
    () => vendaOfflinePagamentos.reduce((s, p) => s + p.valor, 0),
    [vendaOfflinePagamentos]
  );

  // Quando fica offline e há venda em andamento, entra em modoOffline
  useEffect(() => {
    if (!online && vendaId && !modoOffline) {
      toast.warning('Conexão perdida. Continuando em modo offline.');
      setModoOffline(true);
    }
    if (online && modoOffline) {
      setModoOffline(false);
    }
  }, [online, vendaId, modoOffline]);

  // Busca com debounce
  const [termoProduto,  setTermoProduto]  = useState('');
  const [termoCliente,  setTermoCliente]  = useState('');
  const [buscaAtivaProd, setBuscaAtivaProd] = useState('');
  const [buscaAtivaCliente, setBuscaAtivaCliente] = useState('');

  const { data: produtosEncontrados } = useBuscarProduto(buscaAtivaProd);
  const { data: clientesEncontrados  } = useBuscarCliente(buscaAtivaCliente);

  // Painel de pagamento
  const [formaSel,    setFormaSel]    = useState<FormaPagamentoPdv>(FormaPagamentoPdv.Dinheiro);
  const [valorPag,    setValorPag]    = useState('');
  const [parcelasSel, setParcelasSel] = useState(1);

  // Modal de sucesso pós-finalização
  const [vendaFinalizada, setVendaFinalizada] = useState<Venda | null>(null);

  // Cliente associado à venda
  const [clienteSelecionado, setClienteSelecionado] = useState<ClientePdv | null>(null);

  // Modal e estado de estorno (T2.4)
  const [modalEstornar,    setModalEstornar]    = useState(false);
  const [motivoEstorno,    setMotivoEstorno]    = useState('');
  const [resultadoEstorno, setResultadoEstorno] = useState<EstornoResultado | null>(null);

  // ── Turno de caixa (T2.3) ─────────────────────────────────────────────
  const { data: turnoAtual, isLoading: carregandoTurno } = useQuery<Turno | null>({
    queryKey: ['turno-atual'],
    queryFn: () => pdvService.obterTurnoAtual(),
    retry: false,
  });

  // Caixa autoritativo: o do turno aberto; senão, o configurado na estação
  const caixaId = turnoAtual?.caixaId ?? (parseInt(caixaIdStr, 10) || 0);

  // Cache offline (T2.6) — só habilita quando há turno aberto
  usePdvOfflineCache(empresaId, turnoAtual != null && empresaId > 0);

  // Modal de abertura de turno (auto-exibe se não há turno)
  const [modalAbrirTurno, setModalAbrirTurno] = useState(false);
  const [fundoCaixa,      setFundoCaixa]      = useState('0');

  // Modal de fechamento de turno
  const [modalFecharTurno, setModalFecharTurno] = useState(false);
  const [resumoFechamento, setResumoFechamento] = useState<TurnoResumo | null>(null);
  const [contagens, setContagens] = useState({
    dinheiro: '', debito: '', credito: '', pix: '', voucher: '',
  });
  const [obsFechar, setObsFechar] = useState('');

  // Modal sangria/suprimento
  const [modalMovimento, setModalMovimento] = useState<'sangria' | 'suprimento' | null>(null);
  const [valorMovimento, setValorMovimento] = useState('');
  const [motivoMovimento, setMotivoMovimento] = useState('');

  // Auto-exibe modal de abertura quando não há turno
  useEffect(() => {
    if (!carregandoTurno && turnoAtual === null) {
      setModalAbrirTurno(true);
    }
  }, [carregandoTurno, turnoAtual]);

  const mutAbrirTurno = useMutation({
    mutationFn: (dto: { empresaId: number; caixaId: number; fundoCaixa: number }) =>
      pdvService.abrirTurno(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['turno-atual'] });
      setModalAbrirTurno(false);
      toast.success('Caixa aberto com sucesso.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mutFecharTurno = useMutation({
    mutationFn: (dto: Parameters<typeof pdvService.fecharTurno>[1]) =>
      pdvService.fecharTurno(turnoAtual!.id, dto),
    onSuccess: (resumo) => {
      qc.invalidateQueries({ queryKey: ['turno-atual'] });
      setResumoFechamento(resumo);
      setModalFecharTurno(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mutMovimento = useMutation({
    mutationFn: ({ tipo, valor, motivo }: { tipo: 'sangria' | 'suprimento'; valor: number; motivo?: string }) =>
      tipo === 'sangria'
        ? pdvService.sangria(turnoAtual!.id, { valor, motivo })
        : pdvService.suprimento(turnoAtual!.id, { valor, motivo }),
    onSuccess: (_, { tipo }) => {
      setModalMovimento(null);
      setValorMovimento('');
      setMotivoMovimento('');
      toast.success(tipo === 'sangria' ? 'Sangria registrada.' : 'Suprimento registrado.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleAbrirTurno = useCallback(() => {
    if (empresaId <= 0) { toast.error('Empresa não identificada. Faça login novamente.'); return; }
    const numCaixa = parseInt(caixaIdStr, 10);
    if (!numCaixa || numCaixa <= 0) { toast.warning('Informe o número do caixa.'); return; }
    localStorage.setItem(CAIXA_STORAGE_KEY, String(numCaixa));
    const fundo = parseFloat(fundoCaixa) || 0;
    mutAbrirTurno.mutate({ empresaId, caixaId: numCaixa, fundoCaixa: fundo });
  }, [fundoCaixa, mutAbrirTurno, empresaId, caixaIdStr]);

  const handleIniciarFechamento = useCallback(async () => {
    if (!turnoAtual) return;

    // PDV-13: vendas offline pendentes precisam entrar ANTES do fechamento —
    // senão o esperado do turno é calculado sem elas e a diferença fica errada.
    let pendentes = await offlineDb.contarPendentes();
    if (pendentes > 0 && navigator.onLine) {
      toast.info(`Sincronizando ${pendentes} venda(s) offline antes do fechamento…`);
      await sincronizarVendasPendentes();
      pendentes = await offlineDb.contarPendentes();
    }
    if (pendentes > 0) {
      toast.warning(
        `Há ${pendentes} venda(s) offline ainda não sincronizada(s). ` +
        'Sincronize antes de fechar o caixa — elas pertencem a este turno.',
      );
      return;
    }

    const resumo = await pdvService.obterResumoTurno(turnoAtual.id);
    setContagens({
      dinheiro: String(resumo.esperadoDinheiro),
      debito:   String(resumo.esperadoDebito),
      credito:  String(resumo.esperadoCredito),
      pix:      String(resumo.esperadoPix),
      voucher:  String(resumo.esperadoVoucher),
    });
    setModalFecharTurno(true);
  }, [turnoAtual]);

  const handleConfirmarFechamento = useCallback(() => {
    mutFecharTurno.mutate({
      contadoDinheiro: parseFloat(contagens.dinheiro) || 0,
      contadoDebito:   parseFloat(contagens.debito)   || 0,
      contadoCredito:  parseFloat(contagens.credito)  || 0,
      contadoPix:      parseFloat(contagens.pix)      || 0,
      contadoVoucher:  parseFloat(contagens.voucher)  || 0,
      observacao:      obsFechar || undefined,
    });
  }, [contagens, obsFechar, mutFecharTurno]);

  const handleConfirmarMovimento = useCallback(() => {
    if (!modalMovimento) return;
    const valor = parseFloat(valorMovimento);
    if (!valor || valor <= 0) { toast.warning('Informe um valor positivo.'); return; }
    mutMovimento.mutate({ tipo: modalMovimento, valor, motivo: motivoMovimento || undefined });
  }, [modalMovimento, valorMovimento, motivoMovimento, mutMovimento]);

  // Foco automático no campo de busca (leitor de código de barras)
  const inputBuscaRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (vendaId) inputBuscaRef.current?.focus();
  }, [vendaId, venda?.itens.length]);

  // ── Iniciar venda ───────────────────────────────────────────────────────
  const handleAbrirVenda = useCallback(async () => {
    if (!navigator.onLine) {
      // Offline: cria venda local
      const idLocal = uuidv4();
      setVendaOfflineId(idLocal);
      setVendaOfflineItens([]);
      setVendaOfflinePagamentos([]);
      setVendaOfflineFinalizada(false);
      setModoOffline(true);
      // Usa -1 como sentinela de venda offline (não bate com IDs do servidor)
      iniciarNovaSessao(-1);
      return;
    }
    if (empresaId <= 0) { toast.error('Empresa não identificada. Faça login novamente.'); return; }
    const v = await ops.abrirVenda.mutateAsync({ empresaId, caixaId });
    iniciarNovaSessao(v.id);
  }, [ops.abrirVenda, iniciarNovaSessao, empresaId, caixaId]);

  // ── Buscar produto ──────────────────────────────────────────────────────
  const handleBuscaProdutoKeyDown = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const termo = termoProduto.trim();
    if (!navigator.onLine || modoOffline) {
      // Busca no IndexedDB
      const res = await offlineDb.buscarProdutoCache(termo);
      setProdutosOfflineCache(res as ProdutoPdv[]);
    } else {
      setBuscaAtivaProd(termo);
    }
  }, [termoProduto, modoOffline]);

  const handleSelecionarProduto = useCallback(async (prod: ProdutoPdv) => {
    if (!vendaId) return;

    if (modoOffline || vendaId === -1) {
      // Offline: adiciona ao estado local
      setVendaOfflineItens(prev => {
        const existente = prev.find(i => i.produtoId === prod.id);
        if (existente) {
          return prev.map(i => i.produtoId === prod.id
            ? { ...i, quantidade: i.quantidade + 1 }
            : i);
        }
        return [...prev, {
          produtoId: prod.id,
          quantidade: 1,
          precoUnitario: prod.precoUnitario,
          descricao: prod.descricao,
        }];
      });
      setProdutosOfflineCache([]);
      setTermoProduto('');
      inputBuscaRef.current?.focus();
      return;
    }

    await ops.adicionarItem.mutateAsync({ dto: { produtoId: prod.id, quantidade: 1 } });
    setTermoProduto('');
    setBuscaAtivaProd('');
    inputBuscaRef.current?.focus();
  }, [vendaId, ops.adicionarItem, modoOffline]);

  // ── Buscar cliente ──────────────────────────────────────────────────────
  const handleBuscaClienteKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') setBuscaAtivaCliente(termoCliente.trim());
  }, [termoCliente]);

  const mutAssociarCliente = useMutation({
    mutationFn: (cli: ClientePdv) => pdvService.associarCliente(vendaId!, cli.id),
    onSuccess: (_, cli) => {
      setClienteSelecionado(cli);
      qc.invalidateQueries({ queryKey: ['venda', vendaId] });
      toast.success(`Cliente ${cli.nome} associado.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mutEstornar = useMutation({
    mutationFn: (motivo: string) => pdvService.estornar(vendaId!, motivo),
    onSuccess: (resultado) => {
      setModalEstornar(false);
      setMotivoEstorno('');
      setResultadoEstorno(resultado);
      qc.invalidateQueries({ queryKey: ['venda', vendaId] });
    },
    onError: (e: Error) => { toast.error(e.message); },
  });

  const handleSelecionarCliente = useCallback((cli: ClientePdv) => {
    if (!vendaId) return;
    mutAssociarCliente.mutate(cli);
    setBuscaAtivaCliente('');
    setTermoCliente('');
  }, [vendaId, mutAssociarCliente]);

  const handleRemoverCliente = useCallback(() => {
    setClienteSelecionado(null);
  }, []);

  const handleConfirmarEstorno = useCallback(() => {
    if (motivoEstorno.trim().length < 15) {
      toast.warning('O motivo deve ter no mínimo 15 caracteres.');
      return;
    }
    mutEstornar.mutate(motivoEstorno.trim());
  }, [motivoEstorno, mutEstornar]);

  // ── Adicionar pagamento ─────────────────────────────────────────────────
  const handleAdicionarPagamento = useCallback(async () => {
    if (!valorPag || parseFloat(valorPag) <= 0) {
      toast.warning('Informe o valor do pagamento.');
      return;
    }
    const valor = parseFloat(valorPag);

    // PDV-10: só dinheiro pode exceder o total (troco). Cartão/PIX/voucher acima
    // do restante seriam rejeitados pelo servidor — offline, a venda nunca
    // sincronizaria, então o bloqueio precisa acontecer já aqui.
    const offline = modoOffline || vendaId === -1;
    const totalVenda = offline ? totalOffline : (venda?.totalLiquido ?? 0);
    const pagoAtual = offline
      ? vendaOfflinePagamentos.reduce((s, p) => s + p.valor, 0)
      : (venda?.pagamentos.reduce((s, p) => s + p.valor, 0) ?? 0);
    if (formaSel !== FormaPagamentoPdv.Dinheiro && pagoAtual + valor > totalVenda) {
      toast.warning('Apenas pagamento em dinheiro pode exceder o total da venda (troco).');
      return;
    }

    if (offline) {
      setVendaOfflinePagamentos(prev => [
        ...prev,
        { formaPagamento: formaSel, valor, parcelas: parcelasSel },
      ]);
      setValorPag('');
      return;
    }

    await ops.adicionarPagamento.mutateAsync({
      formaPagamento: formaSel,
      valor,
      parcelas: parcelasSel,
    });
    setValorPag('');
  }, [ops.adicionarPagamento, formaSel, valorPag, parcelasSel, modoOffline, vendaId,
      venda, totalOffline, vendaOfflinePagamentos]);

  // ── Finalizar venda ─────────────────────────────────────────────────────
  const handleFinalizar = useCallback(async () => {
    if (!vendaId) return;

    if (modoOffline || vendaId === -1) {
      // Salva na fila offline
      const idLocal = vendaOfflineId ?? uuidv4();
      await salvarVendaOffline({
        idLocal,
        empresaId,
        caixaId,
        clienteId: clienteSelecionado?.id,
        clienteNome: clienteSelecionado?.nome,
        itens: vendaOfflineItens,
        pagamentos: vendaOfflinePagamentos,
        totalLiquido: totalOffline,
      });
      setVendaOfflineFinalizada(true);
      toast.success('Venda salva — será enviada ao servidor quando a conexão voltar.');
      return;
    }

    const result = await ops.finalizar.mutateAsync();
    setVendaFinalizada(result);
  }, [ops.finalizar, vendaId, modoOffline, vendaOfflineId, vendaOfflineItens, vendaOfflinePagamentos, totalOffline, clienteSelecionado, empresaId, caixaId]);

  const handleNovaVenda = useCallback(() => {
    setVendaFinalizada(null);
    setVendaOfflineFinalizada(false);
    setVendaOfflineItens([]);
    setVendaOfflinePagamentos([]);
    setVendaOfflineId(null);
    encerrarSessao();
  }, [encerrarSessao]);

  // ── Encerrar sessão após estorno completo ───────────────────────────────
  const handleFecharEstorno = useCallback(() => {
    setResultadoEstorno(null);
    setClienteSelecionado(null);
    encerrarSessao();
  }, [encerrarSessao]);

  // ── Tela de boas-vindas (sem venda ativa) ───────────────────────────────
  if (!vendaId) {
    const caixaAberto = turnoAtual != null;
    return (
      <>
        <div className="flex h-screen items-center justify-center bg-muted/30">
          <Card className="w-80 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-6 w-6" />
                Frente de Caixa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!caixaAberto ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    O caixa está fechado. Abra o caixa para iniciar vendas.
                  </p>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => setModalAbrirTurno(true)}
                    disabled={carregandoTurno}
                  >
                    Abrir Caixa
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Nenhuma venda em andamento. Inicie uma nova venda para começar.
                  </p>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleAbrirVenda}
                    disabled={ops.abrirVenda.isPending}
                  >
                    {ops.abrirVenda.isPending ? 'Abrindo...' : 'Nova Venda (F2)'}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleIniciarFechamento}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Fechar Caixa
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
        {renderModaisTurno()}
      </>
    );
  }

  const totalPago   = modoOffline ? totalPagoOffline : (venda?.pagamentos.reduce((s, p) => s + p.valor, 0) ?? 0);
  const totalLiquidoAtual = modoOffline ? totalOffline : (venda?.totalLiquido ?? 0);
  const saldoFalta  = totalLiquidoAtual - totalPago;
  const troco       = Math.max(0, totalPago - totalLiquidoAtual);
  const vendaOk     = saldoFalta <= 0 && (modoOffline ? vendaOfflineItens.length > 0 : (venda?.itens.length ?? 0) > 0);

  // Modal pós-finalização offline
  if (vendaOfflineFinalizada) {
    return (
      <div className="flex h-screen items-center justify-center bg-muted/30">
        <div className="w-80 rounded-lg border bg-card p-6 shadow-lg space-y-4 text-center">
          <CheckCircle className="mx-auto h-10 w-10 text-yellow-500" />
          <h2 className="text-lg font-semibold">Venda Salva Offline</h2>
          <p className="text-sm text-muted-foreground">
            A venda foi salva localmente e será enviada ao servidor automaticamente quando a conexão voltar.
          </p>
          <div className="rounded-md bg-muted px-3 py-2 text-sm">
            Total: <strong>{fmt(totalOffline)}</strong>
          </div>
          <Button className="w-full" onClick={handleNovaVenda}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Venda
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="flex h-screen overflow-hidden bg-background flex-col">
      <BannerConexao statusConexao={statusConexao} vendasPendentes={vendasPendentes} conflitos={conflitos} />

      {/* Layout interno em duas colunas */}
      <div className="flex flex-1 overflow-hidden">
      {/* ═══════════════════════════════════════════════════════════════════
          COLUNA ESQUERDA — 60% — Produtos e itens da venda
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="flex w-3/5 flex-col gap-3 overflow-y-auto border-r p-4">

        {/* Campo busca / leitor de código de barras */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputBuscaRef}
            placeholder="Código de barras ou descrição (Enter para buscar)"
            value={termoProduto}
            onChange={(e) => setTermoProduto(e.target.value)}
            onKeyDown={handleBuscaProdutoKeyDown}
            className="pl-9 text-base"
            autoFocus
          />
        </div>

        {/* Resultados de busca de produto (online ou offline) */}
        {(modoOffline ? produtosOfflineCache : produtosEncontrados)?.length! > 0 && (
          <Card className={`border-primary/40 ${modoOffline ? 'border-yellow-400' : ''}`}>
            {modoOffline && (
              <div className="px-3 pt-2 text-xs text-yellow-700">Resultados do cache local</div>
            )}
            <CardContent className="p-2">
              {(modoOffline ? produtosOfflineCache : produtosEncontrados)!.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelecionarProduto(p)}
                  className="flex w-full items-center justify-between rounded px-3 py-2 text-left hover:bg-accent"
                >
                  <div>
                    <p className="font-medium">{p.descricao}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.codigoBarras} · Estoque: {p.estoqueAtual.toFixed(2)}
                    </p>
                  </div>
                  <span className="font-semibold text-primary">{fmt(p.precoUnitario)}</span>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Lista de itens */}
        <div className="flex-1">
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">
            Itens da Venda {modoOffline ? '(offline)' : `#${vendaId}`}
          </h2>

          {modoOffline ? (
            vendaOfflineItens.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                Nenhum item adicionado
              </div>
            ) : (
              <div className="space-y-2">
                {vendaOfflineItens.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 rounded-lg border bg-card px-4 py-3">
                    <div className="flex-1">
                      <p className="font-medium">{item.descricao}</p>
                      <p className="text-xs text-muted-foreground">
                        {fmt(item.precoUnitario)} × {item.quantidade}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => setVendaOfflineItens(prev => prev.map((i, k) => k === idx ? { ...i, quantidade: Math.max(1, i.quantidade - 1) } : i))}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center text-sm font-semibold">{item.quantidade}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => setVendaOfflineItens(prev => prev.map((i, k) => k === idx ? { ...i, quantidade: i.quantidade + 1 } : i))}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <span className="w-24 text-right font-semibold">{fmt(item.precoUnitario * item.quantidade)}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => setVendaOfflineItens(prev => prev.filter((_, k) => k !== idx))}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )
          ) : carregandoVenda ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : venda?.itens.length === 0 ? (
            <div className="flex h-32 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
              Nenhum item adicionado
            </div>
          ) : (
            <div className="space-y-2">
              {venda?.itens.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg border bg-card px-4 py-3"
                >
                  <div className="flex-1">
                    <p className="font-medium">Produto #{item.produtoId}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmt(item.precoUnitario)} × {item.quantidade}
                      {item.desconto > 0 && ` − ${fmt(item.desconto)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => ops.alterarQuantidade.mutate({ itemId: item.id, quantidade: Math.max(1, item.quantidade - 1) })}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-8 text-center text-sm font-semibold">{item.quantidade}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => ops.alterarQuantidade.mutate({ itemId: item.id, quantidade: item.quantidade + 1 })}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <span className="w-24 text-right font-semibold">{fmt(item.total)}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => ops.removerItem.mutate(item.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totalizador */}
        <Card>
          <CardContent className="p-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{fmt(modoOffline ? totalOffline : (venda?.totalBruto ?? 0))}</span>
            </div>
            {!modoOffline && (venda?.totalDesconto ?? 0) > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Desconto</span>
                <span>− {fmt(venda!.totalDesconto)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span>{fmt(totalLiquidoAtual)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          COLUNA DIREITA — 40% — Cliente + Pagamento + Ações
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="flex w-2/5 flex-col gap-3 overflow-y-auto p-4">

        {/* Botões de turno */}
        {turnoAtual && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              disabled={!online}
              title={online ? undefined : 'Requer conexão com o servidor'}
              onClick={() => { setModalMovimento('sangria'); setValorMovimento(''); setMotivoMovimento(''); }}
            >
              <ArrowDownFromLine className="mr-1 h-3 w-3" />
              Sangria
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              disabled={!online}
              title={online ? undefined : 'Requer conexão com o servidor'}
              onClick={() => { setModalMovimento('suprimento'); setValorMovimento(''); setMotivoMovimento(''); }}
            >
              <ArrowUpFromLine className="mr-1 h-3 w-3" />
              Suprimento
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs text-destructive hover:text-destructive"
              disabled={!online}
              title={online ? undefined : 'Requer conexão com o servidor'}
              onClick={handleIniciarFechamento}
            >
              <LogOut className="mr-1 h-3 w-3" />
              Fechar Cx.
            </Button>
          </div>
        )}

        {/* Cliente */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4" /> Identificação do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pb-3">
            {clienteSelecionado ? (
              <div className="flex items-center justify-between rounded-md border bg-primary/5 px-3 py-2">
                <div>
                  <p className="text-sm font-semibold">{clienteSelecionado.nome}</p>
                  {clienteSelecionado.cpfCnpj && (
                    <p className="text-xs text-muted-foreground">{clienteSelecionado.cpfCnpj}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={handleRemoverCliente}
                  title="Remover cliente (Consumidor Final)"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="CPF/CNPJ, nome ou telefone"
                    value={termoCliente}
                    onChange={(e) => setTermoCliente(e.target.value)}
                    onKeyDown={handleBuscaClienteKeyDown}
                    className="pl-9"
                  />
                </div>
                {clientesEncontrados && clientesEncontrados.length > 0 && (
                  <div className="rounded border bg-card">
                    {clientesEncontrados.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleSelecionarCliente(c)}
                        className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                        disabled={mutAssociarCliente.isPending}
                      >
                        <span className="font-medium">{c.nome}</span>
                        <span className="text-xs text-muted-foreground">{c.cpfCnpj} {c.telefone}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!termoCliente && (
                  <p className="text-xs text-muted-foreground">Consumidor Final (sem identificação)</p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Pagamento */}
        <Card className="flex-1">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-sm">Pagamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pb-3">
            {/* Botões de forma */}
            <div className="grid grid-cols-3 gap-2">
              {Object.values(FormaPagamentoPdv)
                .filter((v) => typeof v === 'number')
                .map((forma) => {
                  const f = forma as FormaPagamentoPdv;
                  return (
                    <Button
                      key={f}
                      variant={formaSel === f ? 'default' : 'outline'}
                      className="flex flex-col gap-1 h-14 text-xs"
                      onClick={() => setFormaSel(f)}
                    >
                      {ICONES_FORMA[f]}
                      {FormaPagamentoLabel[f]}
                    </Button>
                  );
                })}
            </div>

            {/* Valor e parcelas */}
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Valor (R$)"
                value={valorPag}
                onChange={(e) => setValorPag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdicionarPagamento()}
                min={0}
                step={0.01}
                className="flex-1"
              />
              {formaSel === FormaPagamentoPdv.CartaoCredito && (
                <Select value={String(parcelasSel)} onValueChange={(v) => setParcelasSel(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="x" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}×</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={handleAdicionarPagamento} disabled={ops.adicionarPagamento.isPending}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Pagamentos adicionados */}
            {modoOffline ? (
              vendaOfflinePagamentos.length > 0 && (
                <div className="space-y-1">
                  {vendaOfflinePagamentos.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between rounded border px-3 py-1.5 text-sm">
                      <span className="flex items-center gap-2">
                        {ICONES_FORMA[p.formaPagamento]}
                        {FormaPagamentoLabel[p.formaPagamento]}
                        {(p.parcelas ?? 1) > 1 && <Badge variant="secondary">{p.parcelas}×</Badge>}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{fmt(p.valor)}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive"
                          onClick={() => setVendaOfflinePagamentos(prev => prev.filter((_, k) => k !== idx))}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              (venda?.pagamentos.length ?? 0) > 0 && (
                <div className="space-y-1">
                  {venda!.pagamentos.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded border px-3 py-1.5 text-sm">
                      <span className="flex items-center gap-2">
                        {ICONES_FORMA[p.formaPagamento]}
                        {p.formaPagamentoNome}
                        {p.parcelas > 1 && <Badge variant="secondary">{p.parcelas}×</Badge>}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{fmt(p.valor)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          onClick={() => ops.removerPagamento.mutate(p.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Resumo troco/falta */}
            <Separator />
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pago</span>
                <span className="font-medium">{fmt(totalPago)}</span>
              </div>
              {saldoFalta > 0 && (
                <div className="flex justify-between text-destructive font-semibold">
                  <span>Falta</span>
                  <span>{fmt(saldoFalta)}</span>
                </div>
              )}
              {troco > 0 && (
                <div className="flex justify-between text-green-600 font-semibold">
                  <span>Troco</span>
                  <span>{fmt(troco)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Botões de ação */}
        <div className="flex gap-2">
          {/* Estornar Venda */}
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => { setMotivoEstorno(''); setModalEstornar(true); }}
          >
            <XCircle className="mr-2 h-4 w-4" /> Estornar
          </Button>

          {/* Finalizar */}
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            disabled={!vendaOk || ops.finalizar.isPending}
            onClick={handleFinalizar}
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            {ops.finalizar.isPending ? 'Finalizando...' : `Finalizar (F12)`}
          </Button>
        </div>

        {/* Status da venda */}
        {!modoOffline && venda?.status === StatusPdvVenda.Finalizada && (
          <Badge className="justify-center bg-green-100 text-green-800 py-2">
            Venda finalizada com sucesso!
          </Badge>
        )}
      </div>
      </div>{/* fim layout duas colunas */}
    </div>

    {/* ═══════════════════════════════════════════════════════════════════
        MODAL DE SUCESSO — exibido após finalização bem-sucedida
    ═══════════════════════════════════════════════════════════════════ */}
    <Dialog open={!!vendaFinalizada} onOpenChange={(open) => !open && handleNovaVenda()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-700">
            <CheckCircle className="h-5 w-5" />
            Venda Finalizada!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Venda nº</span>
            <span className="font-semibold">#{vendaFinalizada?.id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total</span>
            <span className="font-semibold">{fmt(vendaFinalizada?.totalLiquido ?? 0)}</span>
          </div>
          {(vendaFinalizada?.troco ?? 0) > 0 && (
            <div className="flex justify-between text-sm text-green-700 font-bold">
              <span>Troco</span>
              <span>{fmt(vendaFinalizada!.troco)}</span>
            </div>
          )}
          {vendaFinalizada?.notaEletronicaId ? (
            <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-xs text-green-800">
              <CheckCircle className="h-3 w-3 flex-shrink-0" />
              NFC-e emitida (nº {vendaFinalizada.notaEletronicaId})
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
              <AlertTriangle className="h-3 w-3 flex-shrink-0" />
              NFC-e em contingência — transmitir quando SEFAZ retornar
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {vendaFinalizada?.notaEletronicaId && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open(`/api/nfce/${vendaFinalizada.notaEletronicaId}/danfe`, '_blank')}
            >
              <Printer className="mr-2 h-4 w-4" />
              Imprimir DANFE NFC-e
            </Button>
          )}
          <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleNovaVenda}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Venda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ═══════════════════════════════════════════════════════════════════
        MODAL ESTORNAR VENDA (T2.4)
    ═══════════════════════════════════════════════════════════════════ */}
    <Dialog open={modalEstornar} onOpenChange={(o) => !o && setModalEstornar(false)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" />
            Estornar Venda #{vendaId}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {venda?.notaEletronicaId && (
            <div className="flex items-start gap-2 rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
              <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
              <span>
                Esta venda possui NFC-e vinculada. O estorno solicitará o cancelamento junto à SEFAZ
                {venda.dtFechamento && new Date(venda.dtFechamento) < new Date(Date.now() - 30 * 60_000)
                  ? ' — prazo de cancelamento expirado, nota será inutilizada.'
                  : '.'}
              </span>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Motivo do estorno <span className="text-destructive">*</span>
            </label>
            <textarea
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="Descreva o motivo do estorno (mínimo 15 caracteres)"
              value={motivoEstorno}
              onChange={(e) => setMotivoEstorno(e.target.value)}
            />
            <p className={`text-xs ${motivoEstorno.trim().length >= 15 ? 'text-muted-foreground' : 'text-destructive'}`}>
              {motivoEstorno.trim().length}/15 caracteres mínimos
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setModalEstornar(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmarEstorno}
            disabled={mutEstornar.isPending || motivoEstorno.trim().length < 15}
          >
            {mutEstornar.isPending ? 'Estornando...' : 'Confirmar Estorno'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ═══════════════════════════════════════════════════════════════════
        MODAL RESULTADO DO ESTORNO
    ═══════════════════════════════════════════════════════════════════ */}
    <Dialog open={!!resultadoEstorno} onOpenChange={(o) => !o && handleFecharEstorno()}>
      {resultadoEstorno && (
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${resultadoEstorno.sucesso ? 'text-green-700' : 'text-destructive'}`}>
              {resultadoEstorno.sucesso
                ? <><CheckCircle className="h-5 w-5" /> Estorno Realizado</>
                : <><XCircle className="h-5 w-5" /> Estorno com Erros</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status NFC-e</span>
              <span className="font-semibold">{resultadoEstorno.statusNfce}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estoque estornado</span>
              <span className={resultadoEstorno.estoqueEstornado ? 'text-green-700 font-semibold' : 'text-destructive font-semibold'}>
                {resultadoEstorno.estoqueEstornado ? 'Sim' : 'Não'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Caixa estornada</span>
              <span className={resultadoEstorno.caixaEstornada ? 'text-green-700 font-semibold' : 'text-muted-foreground font-semibold'}>
                {resultadoEstorno.caixaEstornada ? 'Sim' : 'N/A'}
              </span>
            </div>
            {resultadoEstorno.erros.length > 0 && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 space-y-1">
                <p className="text-xs font-semibold text-destructive">Erros parciais:</p>
                {resultadoEstorno.erros.map((e, i) => (
                  <p key={i} className="text-xs text-destructive">{e}</p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={handleFecharEstorno}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Venda
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>

    {renderModaisTurno()}
    </>
  );

  // ── Modais de turno (extraídos para não poluir o JSX principal) ────────
  function renderModaisTurno() {
    return (
      <>
        {/* MODAL ABRIR CAIXA */}
        <Dialog open={modalAbrirTurno} onOpenChange={(o) => { if (!o && turnoAtual) setModalAbrirTurno(false); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Abrir Caixa</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">Número do Caixa</label>
                <Input
                  type="number"
                  placeholder="1"
                  value={caixaIdStr}
                  onChange={(e) => setCaixaIdStr(e.target.value)}
                  min={1}
                  step={1}
                />
                <p className="text-xs text-muted-foreground">
                  Identifica este terminal (fica salvo nesta estação).
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Fundo de Caixa (R$)</label>
                <Input
                  type="number"
                  placeholder="0,00"
                  value={fundoCaixa}
                  onChange={(e) => setFundoCaixa(e.target.value)}
                  min={0}
                  step={0.01}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Valor em dinheiro colocado no caixa para troco.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={handleAbrirTurno}
                disabled={mutAbrirTurno.isPending}
              >
                {mutAbrirTurno.isPending ? 'Abrindo...' : 'Abrir Caixa'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* MODAL FECHAR CAIXA */}
        <Dialog open={modalFecharTurno} onOpenChange={setModalFecharTurno}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LogOut className="h-5 w-5" />
                Fechamento de Caixa
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm">
              <p className="text-muted-foreground text-xs">
                Informe o valor contado em caixa para cada forma de pagamento.
              </p>
              {([
                ['dinheiro', 'Dinheiro'],
                ['debito',   'Cartão Débito'],
                ['credito',  'Cartão Crédito'],
                ['pix',      'PIX'],
                ['voucher',  'Voucher'],
              ] as const).map(([campo, label]) => (
                <div key={campo} className="flex items-center gap-3">
                  <label className="w-32 text-right text-muted-foreground">{label}</label>
                  <Input
                    type="number"
                    placeholder="0,00"
                    value={contagens[campo]}
                    onChange={(e) => setContagens(prev => ({ ...prev, [campo]: e.target.value }))}
                    min={0}
                    step={0.01}
                    className="flex-1"
                  />
                </div>
              ))}
              <div className="flex items-start gap-3 pt-1">
                <label className="w-32 text-right text-muted-foreground pt-2">Observação</label>
                <Input
                  placeholder="Opcional"
                  value={obsFechar}
                  onChange={(e) => setObsFechar(e.target.value)}
                  className="flex-1"
                />
              </div>
              {/* Resumo de diferença estimada */}
              {(() => {
                const total = ['dinheiro','debito','credito','pix','voucher']
                  .reduce((s, k) => s + (parseFloat(contagens[k as keyof typeof contagens]) || 0), 0);
                return (
                  <div className="rounded-md bg-muted px-3 py-2 text-xs">
                    Total contado: <strong>{fmt(total)}</strong>
                  </div>
                );
              })()}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setModalFecharTurno(false)}>Cancelar</Button>
              <Button
                className="bg-destructive hover:bg-destructive/90"
                onClick={handleConfirmarFechamento}
                disabled={mutFecharTurno.isPending}
              >
                {mutFecharTurno.isPending ? 'Fechando...' : 'Confirmar Fechamento'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* MODAL RESUMO APÓS FECHAMENTO */}
        <Dialog open={!!resumoFechamento} onOpenChange={(o) => !o && setResumoFechamento(null)}>
          {resumoFechamento && (
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-700" />
                  Caixa Fechado
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-2 text-sm">
                {([
                  ['Dinheiro',     resumoFechamento.esperadoDinheiro, resumoFechamento.contadoDinheiro],
                  ['Débito',       resumoFechamento.esperadoDebito,   resumoFechamento.contadoDebito],
                  ['Crédito',      resumoFechamento.esperadoCredito,  resumoFechamento.contadoCredito],
                  ['PIX',          resumoFechamento.esperadoPix,      resumoFechamento.contadoPix],
                  ['Voucher',      resumoFechamento.esperadoVoucher,  resumoFechamento.contadoVoucher],
                ] as [string, number, number][]).map(([label, esp, cont]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-muted-foreground w-24">{label}</span>
                    <span className="w-28 text-right">Esp: {fmt(esp)}</span>
                    <span className={`w-28 text-right font-semibold ${cont !== esp ? 'text-destructive' : 'text-green-700'}`}>
                      Cont: {fmt(cont)}
                    </span>
                  </div>
                ))}
                <Separator />
                <div className="flex justify-between font-bold">
                  <span>Diferença</span>
                  <span className={resumoFechamento.diferenca === 0 ? 'text-green-700' : 'text-destructive'}>
                    {resumoFechamento.diferenca >= 0 ? '+' : ''}{fmt(resumoFechamento.diferenca)}
                  </span>
                </div>
              </div>
              <DialogFooter>
                <Button className="w-full" onClick={() => setResumoFechamento(null)}>Fechar</Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>

        {/* MODAL SANGRIA / SUPRIMENTO */}
        <Dialog open={!!modalMovimento} onOpenChange={(o) => !o && setModalMovimento(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {modalMovimento === 'sangria' ? 'Sangria' : 'Suprimento'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">Valor (R$)</label>
                <Input
                  type="number"
                  placeholder="0,00"
                  value={valorMovimento}
                  onChange={(e) => setValorMovimento(e.target.value)}
                  min={0.01}
                  step={0.01}
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Motivo (opcional)</label>
                <Input
                  placeholder={modalMovimento === 'sangria' ? 'Ex: Depósito banco' : 'Ex: Reforço de troco'}
                  value={motivoMovimento}
                  onChange={(e) => setMotivoMovimento(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmarMovimento()}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setModalMovimento(null)}>Cancelar</Button>
              <Button
                onClick={handleConfirmarMovimento}
                disabled={mutMovimento.isPending}
              >
                {mutMovimento.isPending ? 'Registrando...' : 'Confirmar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
}
